import { getNativeSessionModeHeaders, supportsNativeRefreshBroker, } from "./nativeBridge";
const PROD_WIDGET_HOST = "widget2.chio-chio.ru";
const PROD_MOBILE_HOST = "mobile2.chio-chio.ru";
export const NATIVE_LOGIN_HANDOFF_COMMITTED_EVENT = "cc:native-login-export-committed";
const NATIVE_HANDOFF_ID_PATTERN = /^[A-Za-z0-9_-]{16,80}$/;
const AUTH_RETURN_HOSTS = new Set([
    "mobile2.chio-chio.ru",
    "widget2.chio-chio.ru",
    "widget.devchio.ru",
    "lkfr.chio-chio.ru",
    "lkfr.devchio.ru",
    "lkfr-admin.chio-chio.ru",
    "lkfr-admin.devchio.ru",
    "staff.chio-chio.ru",
    "staff.devchio.ru",
    "adminstaff.chio-chio.ru",
    "staff-admin.devchio.ru",
    "designfront.chio-chio.ru",
    "support.devchio.ru",
    "id-admin.chio-chio.ru",
    "id-admin.devchio.ru",
    "wiki.chio-chio.ru",
    "wiki.devchio.ru",
    "maps.chio-chio.ru",
    "mapsapi.chio-chio.ru",
    "maps.devchio.ru",
]);
function trimString(value) {
    return typeof value === "string" ? value.trim() : "";
}
function createNativeHandoffId() {
    try {
        const cryptoApi = window.crypto;
        if (!cryptoApi || typeof cryptoApi.getRandomValues !== "function") {
            return "";
        }
        const bytes = new Uint8Array(24);
        cryptoApi.getRandomValues(bytes);
        return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
    }
    catch {
        return "";
    }
}
function readNativeSessionEpoch() {
    try {
        const raw = window.__CC_NATIVE_SESSION_CONTEXT__?.sessionEpoch;
        return typeof raw === "number" && Number.isFinite(raw) && raw >= 0
            ? Math.floor(raw)
            : undefined;
    }
    catch {
        return undefined;
    }
}
async function postNativeLoginExportWithAck(args) {
    const handoffId = createNativeHandoffId();
    const expectedEpoch = readNativeSessionEpoch();
    if (!NATIVE_HANDOFF_ID_PATTERN.test(handoffId) || expectedEpoch === undefined) {
        return false;
    }
    return new Promise((resolve) => {
        let settled = false;
        let retryTimer;
        let timeoutTimer;
        const finish = (ok) => {
            if (settled)
                return;
            settled = true;
            window.removeEventListener(NATIVE_LOGIN_HANDOFF_COMMITTED_EVENT, handleAck);
            if (retryTimer)
                clearTimeout(retryTimer);
            if (timeoutTimer)
                clearTimeout(timeoutTimer);
            resolve(ok);
        };
        const handleAck = (event) => {
            const detail = event.detail;
            if (detail?.handoffId === handoffId &&
                detail?.epoch === expectedEpoch &&
                readNativeSessionEpoch() === expectedEpoch) {
                finish(true);
            }
        };
        const post = () => args.postToNative("refresh_export_inline", {
            access_token: args.accessToken || "",
            refresh_token: args.refreshToken,
            fp: args.fingerprint,
            src: "id_login_success",
            return_to: args.target,
            handoff_id: handoffId,
        });
        window.addEventListener(NATIVE_LOGIN_HANDOFF_COMMITTED_EVENT, handleAck);
        if (post() === false) {
            finish(false);
            return;
        }
        retryTimer = setTimeout(() => {
            if (!settled)
                post();
        }, Math.min(750, Math.max(100, Math.floor(args.timeoutMs / 3))));
        timeoutTimer = setTimeout(() => finish(false), Math.max(1000, args.timeoutMs));
    });
}
function normalizeFlushResult(value) {
    if (!value || typeof value !== "object") {
        return {};
    }
    return {
        refreshToken: trimString(value.refreshToken),
        fingerprint: trimString(value.fingerprint),
    };
}
function getGlobalSessionStorage() {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : undefined;
    }
    catch {
        return undefined;
    }
}
function readStoredRefreshToken(storage) {
    try {
        return trimString((storage || getGlobalSessionStorage())?.getItem("refresh_token"));
    }
    catch {
        return "";
    }
}
function readNativeSessionBootstrap() {
    try {
        if (typeof window === "undefined") {
            return undefined;
        }
        const value = window.__CC_NATIVE_SESSION_BOOTSTRAP__;
        return value && typeof value === "object" ? value : undefined;
    }
    catch {
        return undefined;
    }
}
function hasNativeBootstrappedSession(value) {
    const bootstrap = value || readNativeSessionBootstrap();
    return Boolean(bootstrap?.hasRefreshToken && bootstrap?.hasFingerprint);
}
function postNativeLoginExport(args) {
    if (!args.refreshToken) {
        return;
    }
    const payload = {
        access_token: args.accessToken || "",
        refresh_token: args.refreshToken || "",
        fp: args.fingerprint,
        src: "id_login_success",
    };
    if (args.supportsNativeLoginRedirect) {
        payload.return_to = args.target;
    }
    args.postToNative("refresh_export_inline", payload);
}
export function getConfiguredWidgetTarget(appFallbackHome) {
    if (!appFallbackHome)
        return "";
    try {
        const target = new URL(appFallbackHome);
        return isAllowedAuthReturnTarget(target.toString()) ? target.toString() : "";
    }
    catch {
        return "";
    }
}
export function isAllowedAuthReturnTarget(rawTarget) {
    const value = trimString(rawTarget);
    if (!value)
        return false;
    try {
        const target = new URL(value);
        const hostname = target.hostname.toLowerCase();
        return (target.protocol === "https:" &&
            target.username === "" &&
            target.password === "" &&
            target.port === "" &&
            AUTH_RETURN_HOSTS.has(hostname));
    }
    catch {
        return false;
    }
}
export function getDefaultWidgetOrigin(args) {
    const configuredTarget = getConfiguredWidgetTarget(args.appFallbackHome);
    if (configuredTarget) {
        try {
            return new URL(configuredTarget).origin;
        }
        catch {
            // ignore
        }
    }
    const protocol = args.protocol === "http:" || args.protocol === "https:" ? args.protocol : "https:";
    const hostname = trimString(args.hostname);
    if (hostname.endsWith(".devchio.ru")) {
        return `${protocol}//widget.devchio.ru`;
    }
    return `${protocol}//widget2.chio-chio.ru`;
}
export function getDefaultWidgetTarget(args) {
    const fallbackSource = getConfiguredWidgetTarget(args.appFallbackHome) ||
        getDefaultWidgetOrigin({
            appFallbackHome: args.appFallbackHome,
            protocol: args.protocol,
            hostname: args.hostname,
        });
    try {
        const url = new URL(fallbackSource, args.origin || "https://widget2.chio-chio.ru");
        if (url.pathname === "/" || url.pathname === "") {
            url.pathname = "/home";
        }
        return url.toString();
    }
    catch {
        const normalized = fallbackSource.replace(/\/$/, "");
        return `${normalized}/home`;
    }
}
export function getRedirectTarget(args) {
    try {
        const url = new URL(args.href);
        const redir = url.searchParams.get("returnTo");
        if (redir && isAllowedAuthReturnTarget(redir)) {
            return new URL(redir).toString();
        }
    }
    catch {
        // ignore
    }
    return getDefaultWidgetTarget({
        appFallbackHome: args.appFallbackHome,
        origin: args.origin,
        protocol: args.protocol,
        hostname: args.hostname,
    });
}
export function normalizeMobileReturnTarget(target, isNativeWebView) {
    if (!isNativeWebView) {
        return target;
    }
    try {
        const url = new URL(target);
        if (url.protocol === "https:" && url.hostname === PROD_WIDGET_HOST) {
            url.hostname = PROD_MOBILE_HOST;
            return url.toString();
        }
    }
    catch {
        // keep the original target
    }
    return target;
}
export async function cookieFlushPing(args) {
    let result = {};
    const fetcher = args.fetchImpl || fetch;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    await sleep(args.firstDelayMs ?? 150);
    try {
        if (args.isInWebView) {
            const response = await fetcher(`${args.apiBase}/api/refresh/export`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "X-CSRF-Token": args.fingerprint,
                    "X-Client": "mobile",
                    "Cache-Control": "no-store",
                    ...getNativeSessionModeHeaders(),
                },
            }).catch(() => { });
            if (response && "ok" in response && response.ok && typeof response.json === "function") {
                const data = await response.json().catch(() => null);
                result = {
                    refreshToken: trimString(data?.refresh_token),
                    fingerprint: trimString(data?.fp),
                };
            }
            if (!result.refreshToken && hasNativeBootstrappedSession(args.nativeSessionBootstrap)) {
                const refreshToken = readStoredRefreshToken(args.sessionStorage);
                if (refreshToken) {
                    const refreshResponse = await fetcher(`${args.apiBase}/api/refresh`, {
                        method: "POST",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": args.fingerprint,
                            "X-Refresh-Token": refreshToken,
                            "X-Client": "mobile",
                            "Cache-Control": "no-store",
                            ...getNativeSessionModeHeaders(),
                        },
                        body: JSON.stringify({
                            refresh_token: refreshToken,
                        }),
                    }).catch(() => { });
                    if (refreshResponse &&
                        "ok" in refreshResponse &&
                        refreshResponse.ok &&
                        typeof refreshResponse.json === "function") {
                        const data = await refreshResponse.json().catch(() => null);
                        result = {
                            refreshToken: trimString(data?.refresh_token) || refreshToken,
                            fingerprint: args.fingerprint,
                        };
                    }
                }
            }
        }
    }
    catch {
        // ignore
    }
    await sleep(args.secondDelayMs ?? 200);
    return result;
}
export async function finalizeLoginFlow(args) {
    const target = normalizeMobileReturnTarget(getRedirectTarget({
        href: args.location.href,
        appFallbackHome: args.appFallbackHome,
        origin: args.location.origin,
        protocol: args.location.protocol,
        hostname: args.location.hostname,
    }), args.isNativeWebView);
    try {
        if (args.accessToken)
            args.sessionStorage.setItem("access_token", args.accessToken);
    }
    catch {
        // ignore
    }
    const nativeBroker = Boolean(args.isNativeWebView &&
        (args.supportsNativeRefreshBroker ?? supportsNativeRefreshBroker()));
    if (nativeBroker) {
        try {
            args.sessionStorage.removeItem?.("refresh_token");
        }
        catch {
            // ignore
        }
        const exported = args.refreshToken
            ? {}
            : normalizeFlushResult(await args.flushCookies(args.fingerprint));
        const refreshToken = args.refreshToken || exported.refreshToken || "";
        const fingerprint = exported.fingerprint || args.fingerprint;
        if (!refreshToken || !fingerprint) {
            throw new Error("native_session_handoff_incomplete");
        }
        const committed = await postNativeLoginExportWithAck({
            accessToken: args.accessToken,
            refreshToken,
            fingerprint,
            target,
            timeoutMs: args.nativeHandoffTimeoutMs ?? 5000,
            postToNative: args.postToNative,
        });
        try {
            args.sessionStorage.removeItem?.("refresh_token");
        }
        catch {
            // ignore
        }
        if (!committed) {
            throw new Error("native_session_handoff_not_committed");
        }
        try {
            args.location.replace?.(target);
        }
        catch {
            args.assignHref(target);
        }
        return target;
    }
    try {
        if (args.refreshToken)
            args.sessionStorage.setItem("refresh_token", args.refreshToken);
    }
    catch {
        // ignore
    }
    postNativeLoginExport({
        accessToken: args.accessToken,
        refreshToken: args.refreshToken,
        fingerprint: args.fingerprint,
        target,
        supportsNativeLoginRedirect: args.supportsNativeLoginRedirect,
        postToNative: args.postToNative,
    });
    const exported = normalizeFlushResult(await args.flushCookies(args.fingerprint));
    const finalRefreshToken = args.refreshToken || exported.refreshToken || "";
    const finalFingerprint = exported.fingerprint || args.fingerprint;
    try {
        if (!args.refreshToken && exported.refreshToken) {
            args.sessionStorage.setItem("refresh_token", exported.refreshToken);
        }
    }
    catch {
        // ignore
    }
    postNativeLoginExport({
        accessToken: args.accessToken,
        refreshToken: finalRefreshToken,
        fingerprint: finalFingerprint,
        target,
        supportsNativeLoginRedirect: args.supportsNativeLoginRedirect,
        postToNative: args.postToNative,
    });
    try {
        args.location.replace?.(target);
    }
    catch {
        args.assignHref(target);
    }
    return target;
}
